import { Dropdown, Input, Modal, Popover, message } from "antd";
import { useCallback, useMemo, useEffect, useState } from "react";
import { Icon } from "@terminus/dqh-web-ui";
import { useCurrent } from "@/providers";
// import { getCarbonConfig } from '@/services';
import style from "./style.module.scss";
import { UserCard } from "./user-card";
import { useLocation, useNavigate } from "react-router-dom";
import {
  submitSubscriptionRule,
  queryKeywordSetting,
} from "@/services/subscription";

// 关键词弹窗（及右侧入口）仅在这些页面出现
const KEYWORD_MODAL_PATHS = ["/", "/go", "/s"];

export type TopHeadProps = {};

export const TopHead = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isLogin, role, customerInfo, userInfoSelf, current } = useCurrent();
  const isSupplier = role === "supplier";
  const isBuyer = role === "buyer";
  const [isShowTop, setIsShowTop] = useState(false);
  const [showKeyWord, setShowKeyWord] = useState(false);
  const [allAiKeywords, setAllAiKeywords] = useState<string[]>([]);
  const [selectedAiKeywords, setSelectedAiKeywords] = useState<string[]>([]);
  const [customKeywords, setCustomKeywords] = useState<string[]>([]);
  const [maxLimit, setMaxLimit] = useState<number>(0);
  const [inputValue, setInputValue] = useState("");
  const [keywordSaving, setKeywordSaving] = useState(false);
  const [keywordSettingInfo, setKeywordSettingInfo] = useState<any>(null);

  useEffect(() => {
    if (!isLogin) {
      setKeywordSettingInfo(null);
      return;
    }
    queryKeywordSetting()
      .then((res) => {
        setKeywordSettingInfo(res);
      })
      .catch((err) => {
        console.error("queryKeywordSetting failed:", err);
      });
  }, [isLogin]);

  const KEYWORD_DISMISS_KEY = "dqh_keyword_dismissed_at";
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

  const isKeywordDismissedRecently = (): boolean => {
    try {
      const dismissedAt = localStorage.getItem(KEYWORD_DISMISS_KEY);
      if (!dismissedAt) return false;
      return Date.now() - Number(dismissedAt) < THIRTY_DAYS_MS;
    } catch {
      return false;
    }
  };

  // 关键词弹窗本身不会出现的场景，右侧入口也不展示（不含「下次再说」的 30 天忽略）
  const canOpenKeyword = Boolean(
    isLogin &&
      isSupplier &&
      customerInfo &&
      keywordSettingInfo &&
      !keywordSettingInfo?.customKeywords?.length &&
      KEYWORD_MODAL_PATHS.includes(pathname),
  );

  useEffect(() => {
    setShowKeyWord(
      keywordSettingInfo &&
        !keywordSettingInfo?.customKeywords?.length &&
        customerInfo &&
        isSupplier &&
        isLogin &&
        KEYWORD_MODAL_PATHS?.includes(window.location.pathname) &&
        !isKeywordDismissedRecently(),
    );
  }, [keywordSettingInfo, customerInfo, isLogin, isSupplier]);

  useEffect(() => {
    if (showKeyWord) {
      // 加载已有关键词设置
      const existCustom = (keywordSettingInfo?.customKeywords || [])?.map(
        (item: any) => item.word || item,
      );
      const words = (keywordSettingInfo?.aiKeywords || [])?.map((item: any) =>
        typeof item === "string" ? item : item.word || item,
      );
      setCustomKeywords(existCustom);
      if (keywordSettingInfo?.maxLimit) {
        setMaxLimit(keywordSettingInfo.maxLimit);
      }
      setAllAiKeywords(words);
      setSelectedAiKeywords(words);
    }
  }, [showKeyWord, keywordSettingInfo]);

  const handleAddCustomKeyword = useCallback(() => {
    const val = inputValue.trim();
    if (!val) return;
    if (
      maxLimit > 0 &&
      selectedAiKeywords.length + customKeywords.length >= maxLimit
    ) {
      message.warning(`最多选择 ${maxLimit} 个关键词`);
      setInputValue("");
      return;
    }
    setCustomKeywords([...customKeywords, val]);
    setInputValue("");
  }, [inputValue, maxLimit, selectedAiKeywords, customKeywords]);

  // 根据当前时间获取问候语
  const getGreeting = useCallback(() => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 10) {
      return "早上好";
    } else if (hour >= 10 && hour < 13) {
      return "中午好";
    } else if (hour >= 13 && hour < 18) {
      return "下午好";
    } else if (hour >= 18 && hour < 22) {
      return "晚上好";
    } else {
      return "深夜好";
    }
  }, []);

  const handleLogin = useCallback(() => {
    if (isLogin) {
      return;
    }

    window.location.href = `${
      process.env.LOGIN_URL
    }?gotoUrl=${encodeURIComponent(window.location.href)}`;
  }, [isLogin]);

  const handleRegister = useCallback(() => {
    window.location.href = `${
      process.env.REGISTER_URL
    }?gotoUrl=${encodeURIComponent(window.location.href)}`;
  }, []);

  const helpCenter = useMemo(() => {
    return (
      <>
        <Dropdown
          overlayClassName={`${style.dropdownMenu}`}
          menu={{
            items: [
              {
                label: "服务开通帮助",
                key: "1",
                onClick: () => {
                  window.open(
                    "https://help.daqihui.com/1.0/manual/quick-start/qcs/buy-service.html",
                    "_blank",
                  );
                },
              },
              {
                label: "产品操作手册",
                key: "2",
                onClick: () => {
                  window.open(
                    "https://help.daqihui.com/1.0/manual/quick-start/dqc/operating%20manual/Inquiry%20and%20comparison.html",
                    "_blank",
                  );
                },
              },
              {
                label: "开放平台文档",
                key: "3",
                onClick: () => {
                  window.open(
                    "https://help.daqihui.com/1.0/manual/open/solution/seller-solution.html",
                    "_blank",
                  );
                },
              },
              {
                label: "帮助中心首页",
                key: "4",
                onClick: () => {
                  window.open(
                    "https://help.daqihui.com/1.0/manual/about/daqihui.html",
                    "_blank",
                  );
                },
              },
              {
                label: "平台规则公告",
                key: "5",
                onClick: () => {
                  window.open(
                    "https://help.daqihui.com/1.0/manual/rule-notice/notice/20240422-service-change.html",
                    "_blank",
                  );
                },
              },
            ],
          }}
        >
          <span
            className={style.headerDropdown}
            style={{ cursor: "pointer" }}
            onClick={(e) => e.preventDefault()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span style={{ marginRight: 4 }}>帮助中心</span>
              <Icon name="xiadown" color="#A5A5A5" size={12} />
            </div>
          </span>
        </Dropdown>
        <span className={style.headerLine} />
        <span>0571-26883999（工作日9:00-18:00）</span>
      </>
    );
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = document.documentElement.scrollTop;
      if (scrollTop > 100) {
        setIsShowTop(true);
      } else {
        setIsShowTop(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const rightAffix = useMemo(() => {
    const askContent = (
      <div style={{ display: "flex" }}>
        <div className={style.askContent}>
          <img
            src="https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/img/daqihuiweix.png"
            alt="大企汇微信公众号"
          />
          <span>微信扫码咨询</span>
        </div>
        <div className={style.askContent}>
          <img
            src="https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/img/dingding_qr.png"
            alt="钉钉扫码"
          />
          <span>钉钉扫码咨询</span>
        </div>
      </div>
    );

    const followContent = (
      <div className={style.askContent}>
        <img
          src="https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/img/wx_qr.png"
          alt="关注我们"
        />
        <span>大企汇</span>
        <span>微信公众号</span>
      </div>
    );
    return (
      <div className={style.rightAffix}>
        <div
          className={style.rightAffixItem}
          onClick={() => {
            window.open(
              "https://help.daqihui.com/1.0/manual/quick-start/qcs/buy-service.html",
            );
          }}
        >
          <div className={style.help}></div>
        </div>
        {canOpenKeyword && (
          <div
            className={style.rightAffixItem}
            onClick={() => {
              setShowKeyWord(true);
            }}
          >
            <div className={style.keyword}></div>
          </div>
        )}
        <div className={style.rightAffixItem}>
          <Popover content={askContent} placement="rightTop">
            <div className={style.ask}>
              <img
                src="https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/img/daqihui-pc/advice.png"
                alt="钉钉扫码"
              />
              <span>在线咨询</span>
            </div>
          </Popover>
        </div>
        <div className={style.rightAffixItem}>
          <Popover content={followContent} placement="rightTop">
            <div className={style.follow}></div>
          </Popover>
        </div>
        {isShowTop && (
          <div
            className={style.rightAffixItem}
            onClick={() => {
              window.scrollTo({
                top: 0,
                behavior: "smooth",
              });
            }}
          >
            <div className={style.top}></div>
          </div>
        )}
      </div>
    );
  }, [isShowTop, canOpenKeyword]);

  const keywordModal = (
    <Modal
      open={showKeyWord}
      onCancel={() => setShowKeyWord(false)}
      footer={null}
      width={592}
      centered
      bodyStyle={{ padding: 0, paddingBottom: 38 }}
    >
      {/* 顶部提示区 */}
      <div className={style.keywordModalHeader}>
        <img
          className={style.keywordModalIcon}
          src="https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/img/historyKeyWord.png"
          alt="warn"
        />
        <div className={style.keywordModalTitleWrap}>
          <div className={style.keywordModalTitle}>
            {allAiKeywords?.length > 0
              ? "AI已根据行业数据"
              : "完善您的主营产品/品类/品牌关键词"}
          </div>
          <div className={style.keywordModalTitle}>
            {allAiKeywords?.length > 0
              ? "为您精选了主营产品/品类/品牌词"
              : "获取更准确的商机推荐"}
          </div>
          <div className={style.keywordModalSubTitle}>
            确认后，将自动为您推荐匹配的商机
          </div>
        </div>
      </div>
      {/* 关键词区域 */}
      <div className={style.keywordModalContent}>
        {/* AI 推荐词列表 */}
        {!!allAiKeywords?.length && (
          <div className={style.keywordAiList}>
            {allAiKeywords.map((word, idx) => {
              const isSelected = selectedAiKeywords.includes(word);
              const isOverLimit =
                maxLimit > 0 &&
                selectedAiKeywords.length + customKeywords.length >= maxLimit;
              return (
                <span
                  key={idx}
                  onClick={() => {
                    if (isSelected) {
                      setSelectedAiKeywords(
                        selectedAiKeywords.filter((w) => w !== word),
                      );
                    } else {
                      if (isOverLimit) {
                        message.warning(`最多选择 ${maxLimit} 个关键词`);
                        return;
                      }
                      setSelectedAiKeywords([...selectedAiKeywords, word]);
                    }
                  }}
                  className={`${style.keywordAiTag} ${
                    isSelected ? style.keywordAiTagSelected : ""
                  }`}
                >
                  <span className={style.keywordAiTagText}>{word}</span>
                  <img
                    style={{ height: 10, width: 10, margin: "0 1px" }}
                    src="https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/img/aiActiveKW.png"
                  />
                  {isSelected && (
                    <span className={style.keywordAiTagClose}>×</span>
                  )}
                </span>
              );
            })}
          </div>
        )}
        {/* 自定义关键词 */}
        <div
          className={style.keywordCustomArea}
          style={{
            background: allAiKeywords?.length > 0 ? "#f7f7f7" : "transparent",
          }}
        >
          <span className={style.keywordCustomLabel}>
            {allAiKeywords?.length > 0
              ? "我还想补充（选填）："
              : "我的主营产品/品类/品牌词："}
          </span>
          <div className={style.keywordCustomTags}>
            <Input
              className={style.keywordCustomInput}
              placeholder="自定义关键词"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddCustomKeyword();
                }
              }}
              onBlur={handleAddCustomKeyword}
              suffix={
                <div
                  style={{
                    cursor: "pointer",
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleAddCustomKeyword}
                >
                  +
                </div>
              }
            />
            {customKeywords.map((word, idx) => (
              <span key={idx} className={style.keywordCustomTag}>
                <span>{word}</span>
                <span
                  className={style.keywordAiTagClose}
                  style={{ background: "#A5A5A5" }}
                  onClick={() =>
                    setCustomKeywords(customKeywords.filter((_, i) => i !== idx))
                  }
                >
                  ×
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
      {/* 确定并保存按钮 */}
      <div className={style.keywordSaveBtnWrap}>
        <button
          type="button"
          className={style.keywordSaveBtn}
          disabled={keywordSaving}
          onClick={() => {
            const value = [...selectedAiKeywords, ...customKeywords];
            setKeywordSaving(true);
            if (value?.length > keywordSettingInfo?.maxLimit) {
              message.warning(
                `最多选择 ${keywordSettingInfo?.maxLimit} 个关键词`,
              );
              return;
            }
            submitSubscriptionRule({
              keyWordList: value,
              dislikeKeywordList: allAiKeywords.filter(
                (w) => !selectedAiKeywords.includes(w),
              ),
              source: "OPPORTUNITY_PREFERENCE",
            })
              .then(() => {
                message.success("保存成功");
                setShowKeyWord(false);
                queryKeywordSetting()
                  .then((res) => {
                    setKeywordSettingInfo(res);
                  })
                  .catch((err) => {
                    console.error("queryKeywordSetting failed:", err);
                  });
              })
              .finally(() => {
                setKeywordSaving(false);
              });
          }}
        >
          确认关键词
        </button>
      </div>
      {/* 下次再说 */}
      <div
        className={style.keywordLater}
        onClick={() => {
          try {
            localStorage.setItem(KEYWORD_DISMISS_KEY, String(Date.now()));
          } catch {}
          setShowKeyWord(false);
        }}
      >
        下次再说
      </div>
    </Modal>
  );

  return (
    <>
      {!isLogin ? (
        <div className={style.headerWrapper}>
          <div className={style.headerLeft}>
            <span style={{ marginRight: 8 }}>
              {getGreeting()}， 欢迎来到大企汇
            </span>
            {isLogin ? null : (
              <a
                onClick={handleLogin}
                style={{ marginRight: 8, color: "#FF5600" }}
                className={style.linkPrimary}
              >
                请登录
              </a>
            )}
            <a
              onClick={handleRegister}
              style={{ marginRight: 8, color: "#FF5600" }}
              className={style.linkPrimary}
            >
              快速注册
            </a>
            <a
              style={{ marginRight: 8, color: "#4A4A4C" }}
              className={style.linkDefaultHover}
              href={`${process.env.YCX_HOME}/become-supplier`}
            >
              成为供应商
            </a>
            <a
              style={{ marginRight: 8, color: "#4A4A4C" }}
              className={style.linkDefaultHover}
              href={`${process.env.YCX_HOME}/become-buyer`}
            >
              成为采购商
            </a>
          </div>
          <div className={style.headerRight}>
            <a className={style.linkDefault} href={`${process.env.YCX_HOME}`}>
              大企汇首页
            </a>
            <span className={style.headerLine} />
            {helpCenter}
          </div>
        </div>
      ) : (
        <>
          <div className={style.headerWrapper}>
            <div className={style.headerLeft}>
              <Dropdown
                overlayClassName={`${style.dropdownMenu} ${style.userCardInfo}`}
                menu={{
                  items: [
                    {
                      label: (
                        <UserCard
                          customerInfo={customerInfo}
                          userInfoSelf={userInfoSelf}
                        />
                      ),
                      key: "1",
                    },
                  ],
                }}
              >
                <span
                  className={style.headerDropdown}
                  onClick={(e) => e.preventDefault()}
                  style={{ cursor: "pointer" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <span
                      style={{
                        marginRight: 0,
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {current?.account || "-"}
                    </span>
                    <Icon name="xiadown" color="#4A4A4C" size={12} />
                  </div>
                </span>
              </Dropdown>
              {isBuyer || isSupplier ? (
                <div className={style.headerLeftSpace}>
                  {isSupplier || isBuyer ? (
                    <Icon
                      name="gongzuotai"
                      size={12}
                      style={{ marginTop: -1 }}
                    />
                  ) : null}
                  {isSupplier ? (
                    <span
                      onClick={() => {
                        window.open(
                          `${process.env.QCS_URL}/crm/index_html/home`,
                          "_blank",
                        );
                      }}
                    >
                      企采商工作台
                    </span>
                  ) : null}
                  {isBuyer ? (
                    <span
                      onClick={() => {
                        window.open(`${process.env.BUYER_WEB}`, "_blank");
                      }}
                    >
                      大企采工作台
                    </span>
                  ) : null}
                </div>
              ) : null}
              <a
                style={{ marginRight: 8, color: "#4A4A4C" }}
                className={style.linkDefaultHover}
                href={`${process.env.YCX_HOME}/become-supplier`}
              >
                成为供应商
              </a>
              <a
                style={{ marginRight: 8, color: "#4A4A4C" }}
                className={style.linkDefaultHover}
                href={`${process.env.YCX_HOME}/become-buyer`}
              >
                成为采购商
              </a>
            </div>
            <div className={style.headerRight}>
              <a className={style.linkDefault} href={`${process.env.YCX_HOME}`}>
                大企汇首页
              </a>
              <span className={style.headerLine} />

              {isSupplier ? (
                <>
                  <Dropdown
                    overlayClassName={style.dropdownMenu}
                    menu={{
                      items: [
                        {
                          label: "企业中心",
                          key: "1",
                          onClick: () => {
                            window.open(
                              `${process.env.YCX_HOME}/buyer/supplierCollection`,
                              "_blank",
                            );
                          },
                        },
                        {
                          label: "我的订阅",
                          key: "2",
                          onClick: () => {
                            navigate("/subscription");
                          },
                        },
                        {
                          label: "商机推荐",
                          key: "3",
                          onClick: () => {
                            navigate("/recommend");
                          },
                        },
                      ],
                    }}
                    placement="bottom"
                    autoAdjustOverflow={false}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 4 }}
                    >
                      <Icon name="wodedingyue" size={12} />
                      <a className={style.linkDefault}>企业中心</a>
                      <Icon name="xiadown" color="#A5A5A5" size={12} />
                    </div>
                  </Dropdown>
                  <span className={style.headerLine} />
                </>
              ) : (
                <>
                  <a
                    className={style.linkDefault}
                    href={`${process.env.YCX_HOME}/buyer/supplierCollection`}
                  >
                    企业中心
                  </a>
                  <span className={style.headerLine} />
                </>
              )}

              <Dropdown
                overlayClassName={style.dropdownMenu}
                menu={{
                  items: [
                    {
                      label: "商机收藏",
                      key: "1",
                      onClick: () => {
                        window.open(
                          `${process.env.YCX_HOME}/buyer/businessCollection`,
                          "_blank",
                        );
                      },
                    },
                    {
                      label: "供应商收藏",
                      key: "2",
                      onClick: () => {
                        window.open(
                          `${process.env.YCX_HOME}/buyer/supplierCollection`,
                          "_blank",
                        );
                      },
                    },
                  ],
                }}
                placement="bottom"
                autoAdjustOverflow={false}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    height: 40,
                    cursor: "pointer",
                  }}
                >
                  <Icon name="wodeshoucang" size={12} />
                  <span className={style.linkDefault} style={{ marginLeft: 0 }}>
                    收藏夹
                  </span>
                  <Icon name="xiadown" color="#A5A5A5" size={12} />
                </div>
              </Dropdown>
              <span className={style.headerLine} />
              {helpCenter}
            </div>
          </div>
        </>
      )}
      {rightAffix}
      {keywordModal}
    </>
  );
};
